import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useBlockerAndUnload } from 'shared/ui/Blocker'
import {
  Box,
  Button,
  ContainerHeader,
  Divider,
  EditableTitle,
  Icon,
  IconButton,
  variantActionsProps,
  SectionCollapse,
} from 'shared/ui'
import { layoutStore, type PageLayoutStore } from 'shared/layout'
import { AdditionalBreakpointsEnum } from 'shared/constants/breakpoints'
import { uiStore } from 'shared/store/uiStore'
import { SURVEY_NAME_LIMIT } from 'shared/constants/limits'
import { SurveysPath } from 'entities/ExportRouters'
import { SurveyStatus } from 'entities/Surveys'
import { mobilePreviewModal } from 'widgets/MobilePreview'
import { Preview } from 'pages/surveys/ui/SurveyView/ui/Preview/Preview'
import { SurveyEnrollContacts } from 'pages/surveys/ui/SurveyView/ui/SurveyEnrollContacts/SurveyEnrollContacts'
import { NpsQuestion } from 'pages/surveys/ui/SurveyView/ui/NpsQuestion/NpsQuestion'
import { ScoreElaboration } from 'pages/surveys/ui/SurveyView/ui/ScoreElaboration/ScoreElaboration'
import { ClosingMessage } from 'pages/surveys/ui/SurveyView/ui/ClosingMessage/ClosingMessage'
import { SuccessActions } from 'pages/surveys/ui/SurveyView/ui/SuccessActions/SuccessActions'
import type { SurveyCreateStore } from 'pages/surveys/ui/SurveyView/store/surveyCreateStore'

import styles from './styles.module.scss'
import { FallbackActions } from './FallbackActions/FallbackActions'
import { FallbackAgentActions } from './FallbackActions/FallbackAgentActions'

type ISurveyViewUiProps = {
  store: SurveyCreateStore
  pageLayoutStore: PageLayoutStore
}

export const SurveyViewUi = observer(({ store, pageLayoutStore }: ISurveyViewUiProps) => {
  const {
    isEditMode,
    isActive,
    status,
    nameInputStore,
    enrollContactsStore,
    npsQuestionStore,
    fallbackActionsStore,
    fallbackAgentActionsStore,
    scoreElaborationStore,
    closingMessageStore,
    successActionsStore,
    previewBubbles,
    onSaveSurvey,
    onActivateSurvey,
    disableSurvey,
    blockerStore,
  } = store
  const { name, error: nameError, setName, focusOnMount } = nameInputStore
  const {
    titleIconProps,
    headerMenuTooltipIconProps,
    headerMenuIconAction,
    setCustomCollapseWidth,
  } = pageLayoutStore
  const { isViewXs } = layoutStore

  useBlockerAndUnload(blockerStore)
  useEffect(() => {
    setCustomCollapseWidth(AdditionalBreakpointsEnum['1280'])
    return () => {
      setCustomCollapseWidth(null)
    }
  }, [])

  return (
    <>
      <ContainerHeader
        style={{ height: '100%', minHeight: 'unset', padding: '0 20px' }}
        titleVariant='heading-md'
        titleIconProps={titleIconProps}
        actionIconTooltipProps={headerMenuTooltipIconProps}
        titleIconAction={headerMenuIconAction}
        isBorderBottom
        isTitleDivider
        rightTitleContent={
          status !== null ? (
            <Box marginLeft={'4px'}>
              <SurveyStatus status={status} />
            </Box>
          ) : null
        }
        customTitle={
          <Box display='flex' alignItems='center'>
            <Icon
              icon={'usersCheck'}
              color={'var(--content-brand-primary)'}
              fontSize={20}
              padding='0 8px 0 6px'
            />
            <EditableTitle
              value={name}
              onChange={setName}
              error={nameError}
              limit={SURVEY_NAME_LIMIT}
              isAutoFocus={focusOnMount}
            />
          </Box>
        }
        actionsProps={{
          actions: [
            {
              buttonComponent: (
                <>
                  <IconButton
                    ariaLabel='close_survey'
                    icon='close'
                    variant='contained'
                    color='secondary'
                    size='medium'
                    onClick={() => uiStore.changeRoute({ path: SurveysPath.root })}
                  />
                  <Divider
                    orientation='vertical'
                    sx={{
                      height: '16px !important',
                      marginInline: '3.5px',
                      borderColor: 'var(--border-neutral-default)',
                    }}
                  />
                </>
              ),
            },
            {
              buttonComponent: (
                <Button
                  contained='primary'
                  text={isActive ? 'Publish changes' : 'Activate'}
                  onClick={() => onActivateSurvey()}
                />
              ),
            },
            !isActive && {
              buttonComponent: (
                <Button
                  contained='secondary'
                  text={isEditMode ? 'Save changes' : 'Save as draft'}
                  disabled={isEditMode && !blockerStore.isExistChanges}
                  onClick={() => onSaveSurvey()}
                />
              ),
            },
            (isActive || isViewXs) && {
              dropdownItemComponent: (onCloseMenu) => (
                <>
                  {isActive && (
                    <Button
                      typeBtn='menuItem'
                      text='Disable survey'
                      onClick={() => {
                        disableSurvey()
                        onCloseMenu?.()
                      }}
                    />
                  )}
                  {isViewXs && (
                    <Button
                      typeBtn='menuItem'
                      text='See message preview'
                      onClick={() => {
                        mobilePreviewModal(<Preview bubbles={previewBubbles} />)
                        onCloseMenu?.()
                      }}
                    />
                  )}
                </>
              ),
            },
          ],
          ...variantActionsProps.headerMedium,
          showItems: isActive ? 2 : 3,
          isStrictShowItems: true,
          reverse: true,
        }}
      />
      <div className={styles.wrap}>
        <div className={styles.left}>
          <div className={styles.content}>
            <SurveyEnrollContacts store={enrollContactsStore} />
            <NpsQuestion store={npsQuestionStore} />
            <ScoreElaboration store={scoreElaborationStore} />
            <ClosingMessage store={closingMessageStore} />
            <SectionCollapse
              icon='check2'
              title='Choose success actions'
              tooltipProps={{
                label: 'These actions run as soon as a contact responds with a rating (0-10)',
                placement: 'top',
                breakWord: true,
                maxWidth: 260,
              }}
              error={successActionsStore.sectionError}
            >
              <SuccessActions store={successActionsStore} />
            </SectionCollapse>
            <SectionCollapse
              icon='closeCircle'
              title='Choose fallback actions'
              tooltipProps={{
                label:
                  'These actions run if a contact doesn’t respond or stops before submitting a rating',
                placement: 'top',
                breakWord: true,
                maxWidth: 286,
              }}
              error={fallbackAgentActionsStore.sectionError}
            >
              <FallbackActions store={fallbackActionsStore} />
              <FallbackAgentActions store={fallbackAgentActionsStore} />
            </SectionCollapse>
          </div>
        </div>
        <div className={styles.right}>
          <Preview bubbles={previewBubbles} />
        </div>
      </div>
    </>
  )
})
